import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LogIn } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysFor } from "@/config/cmsSections";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const c = useSiteContent(keysFor("login"));
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [totpCode, setTotpCode] = useState("");
    const [twofa, setTwofa] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        const res = await login(email, password, twofa ? totpCode : undefined);
        setSubmitting(false);
        if (res.ok) {
            toast.success("Connexion réussie");
            // Admin → console admin, sinon espace client classique
            navigate(res.user?.role === "admin" ? "/admin/analytics" : "/dashboard");
        } else if (res.twofaRequired) {
            // Mot de passe validé, il manque le code de l'app d'authentification
            setTwofa(true);
            setError("");
        } else {
            setError(res.error);
        }
    };

    return (
        <div className="em-container py-20 max-w-md" data-testid="login-page">
            <p className="em-eyebrow">{c("login.hero.eyebrow", "Espace client")}</p>
            <h1 className="font-display text-4xl md:text-5xl font-black text-primary mt-2">{c("login.hero.title", "Connexion")}</h1>
            <p className="text-muted-foreground mt-3">{c("login.hero.subtitle", "Accédez à votre dashboard énergie.")}</p>

            <Card className="rounded-3xl border-border mt-8">
                <CardContent className="p-7">
                    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <Label htmlFor="email">{c("login.form.label_email", "Email")}</Label>
                            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl mt-2" data-testid="login-email" />
                        </div>
                        <div>
                            <Label htmlFor="password">{c("login.form.label_password", "Mot de passe")}</Label>
                            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl mt-2" data-testid="login-password" />
                        </div>
                        {twofa && (
                            <div>
                                <Label htmlFor="totp">Code de vérification (app d'authentification)</Label>
                                <Input
                                    id="totp"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    required
                                    autoFocus
                                    placeholder="123456"
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                                    className="rounded-xl mt-2 tracking-[0.3em] text-center text-lg"
                                    data-testid="login-totp"
                                />
                                <p className="text-xs text-muted-foreground mt-2">Saisis le code à 6 chiffres affiché dans ton application (Google Authenticator, Authy…).</p>
                            </div>
                        )}
                        {error && (
                            <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
                        )}
                        <Button type="submit" disabled={submitting} className="w-full rounded-full bg-primary hover:bg-primary/90 h-11" data-testid="login-submit">
                            <LogIn className="h-4 w-4 mr-2" />
                            {submitting ? "Connexion…" : c("login.form.submit", "Se connecter")}
                        </Button>
                    </form>
                    <p className="text-sm text-muted-foreground mt-5 text-center">
                        {c("login.footer.text", "Pas encore de compte ?")}{" "}
                        <Link to="/inscription" className="text-primary font-semibold hover:underline" data-testid="login-to-register">{c("login.footer.link", "Créer un compte")}</Link>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
